import { BiCardDataState, BiCardView } from '@euler/model/bi/dashboard';
import { BiDraftResult, BiDraftView } from '@euler/model/bi/draft';
import { biDisplayFormat } from '@euler/model/bi/format';
import { BiDatasetRefKind } from '@euler/model/bi/query';
import {
  BiCell,
  BiFieldRole,
  BiFieldType,
  BiResultColumn,
} from '@euler/model/bi/result';
import {
  BiFormat,
  BiKpiCompare,
  BiVizSpec,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  kBiComboCardDataCaptured,
  kBiComboDashboardCaptured,
} from '@euler/playground/bi-dashboards/biComboCaptured';
import { kBiBlendDraftCaptured } from '@euler/playground/bi-dashboards/biUploadedCaptured';

const asOf = '2026-10-17T06:30:00.000Z';

const day: BiResultColumn = {
  key: 'created_at__day',
  label: { zh: '日期', en: 'Date' },
  type: BiFieldType.Date,
  role: BiFieldRole.Dimension,
};
const serviceType: BiResultColumn = {
  key: 'service_type',
  label: { zh: '服务类型', en: 'Service type' },
  type: BiFieldType.String,
  role: BiFieldRole.Dimension,
};
const item: BiResultColumn = {
  key: 'item_name',
  label: { zh: '项目', en: 'Item' },
  type: BiFieldType.String,
  role: BiFieldRole.Dimension,
};
const amount: BiResultColumn = {
  key: 'amount',
  label: { zh: '金额', en: 'Amount' },
  type: BiFieldType.Number,
  role: BiFieldRole.Measure,
  format: biDisplayFormat(BiFormat.Currency),
};
const orders: BiResultColumn = {
  key: 'orders',
  label: { zh: '工单数', en: 'Orders' },
  type: BiFieldType.Number,
  role: BiFieldRole.Measure,
  format: biDisplayFormat(BiFormat.Integer),
};
const duration: BiResultColumn = {
  key: 'duration',
  label: { zh: '平均施工时长', en: 'Average job time' },
  type: BiFieldType.Number,
  role: BiFieldRole.Measure,
  format: biDisplayFormat(BiFormat.Duration),
};
const rate: BiResultColumn = {
  key: 'rate',
  label: { zh: '复购率', en: 'Return rate' },
  type: BiFieldType.Number,
  role: BiFieldRole.Measure,
  format: biDisplayFormat(BiFormat.Percent),
};

const days = Array.from(
  { length: 14 },
  (_, i) => `2026-10-${String(i + 1).padStart(2, '0')}`,
);
const kinds = ['常规保养', '钣金喷漆', '轮胎更换'];
const items = [
  '全合成机油更换（含机滤）',
  '四轮定位',
  '刹车片更换',
  '空调滤芯',
  '蓄电池检测与更换',
];

function draft(
  id: string,
  title: string,
  viz: BiVizSpec,
  columns: BiResultColumn[],
  rows: BiCell[][],
  previous?: BiCell[][],
): BiDraftView {
  const result: BiDraftResult = {
    current: { columns, rows, truncated: false, asOf },
    ...(previous
      ? { previous: { columns, rows: previous, truncated: false, asOf } }
      : {}),
  };
  return {
    id: `bidraft_${id.padEnd(26, '0')}`,
    title,
    spec: {
      dataset: { kind: BiDatasetRefKind.Sql, model: 'orders' },
      measures: [],
      dimensions: [],
      filters: [],
      sort: [],
    },
    viz,
    result,
    parentDraftId: null,
    rationale: null,
    createdAt: asOf,
    expiresAt: '2026-11-16T06:30:00.000Z',
  };
}

const daily = days.map((d, i) => [
  d,
  1200000 + Math.round(Math.sin(i) * 300000) + i * 40000,
  30 + ((i * 7) % 13),
]);

export const kBiDraftFixtures: BiDraftView[] = [
  draft(
    'line',
    '近两周每日营收',
    {
      type: BiVizType.Line,
      x: day.key,
      series: [{ key: amount.key }],
    },
    [day, amount, orders],
    daily,
  ),
  draft(
    'area',
    '近两周每日工单数',
    { type: BiVizType.Area, x: day.key, series: [{ key: orders.key }] },
    [day, amount, orders],
    daily,
  ),
  draft(
    'bar',
    '各服务类型营收与工单',
    {
      type: BiVizType.Bar,
      x: serviceType.key,
      series: [{ key: orders.key }, { key: duration.key }],
    },
    [serviceType, orders, duration],
    kinds.map((k, i) => [k, 40 + i * 25, 55 + i * 30]),
  ),
  draft(
    'horizontal',
    '上月最赚钱的服务项目',
    {
      type: BiVizType.HorizontalBar,
      x: item.key,
      series: [{ key: amount.key, name: '营收' }],
    },
    [item, amount],
    items.map((name, i) => [name, 5600000 - i * 900000]),
  ),
  draft(
    'stacked',
    '每周各服务类型营收',
    {
      type: BiVizType.StackedBar,
      x: day.key,
      series: [{ key: amount.key }],
      seriesBy: serviceType.key,
    },
    [day, serviceType, amount],
    days
      .slice(0, 6)
      .flatMap((d, i) =>
        kinds.map((k, j) => [
          d,
          k,
          300000 + (((i + 1) * (j + 2) * 53000) % 400000),
        ]),
      ),
  ),
  draft(
    'pie',
    '营收构成',
    { type: BiVizType.Pie, x: serviceType.key, series: [{ key: amount.key }] },
    [serviceType, amount],
    kinds.map((k, i) => [k, 9000000 - i * 2500000]),
  ),
  draft(
    'donut',
    '工单构成',
    {
      type: BiVizType.Donut,
      x: serviceType.key,
      series: [{ key: orders.key }],
    },
    [serviceType, orders],
    kinds.map((k, i) => [k, 120 - i * 35]),
  ),
  draft(
    'table',
    '服务项目明细',
    {
      type: BiVizType.Table,
      columns: [
        { key: item.key },
        { key: orders.key },
        { key: amount.key },
        { key: rate.key },
      ],
    },
    [item, orders, amount, rate],
    items.map((name, i) => [
      name,
      80 - i * 11,
      5600000 - i * 900000,
      0.42 - i * 0.05,
    ]),
  ),
  draft(
    'kpi',
    '本周营收',
    {
      type: BiVizType.Kpi,
      value: amount.key,
      compare: BiKpiCompare.PreviousPeriod,
    },
    [amount],
    [[8650000]],
    [[7420000]],
  ),
  // Eight-digit amounts, and a group with days missing: gaps, not zeros.
  draft(
    'gaps',
    '近两周各服务类型每日营收',
    {
      type: BiVizType.Line,
      x: day.key,
      series: [{ key: amount.key }],
      seriesBy: serviceType.key,
    },
    [day, serviceType, amount],
    days.flatMap((d, i) =>
      kinds.flatMap((k, j) =>
        j === 1 && i >= 4 && i <= 7
          ? []
          : [
              [
                d,
                k,
                1800000000 + j * 600000000 + Math.round(Math.sin(i + j) * 4e8),
              ],
            ],
      ),
    ),
  ),
  // Monthly orders against an uploaded targets sheet, as the dev rear
  // answered it.
  { ...kBiBlendDraftCaptured, id: `bidraft_${'blend'.padEnd(26, '0')}` },
  // Monthly orders as bars with the average turnaround as a line on the
  // right axis, from a card the dev rear built.
  capturedDraft('combo', kBiComboDashboardCaptured.cards[0]),
];

function capturedDraft(id: string, card: BiCardView): BiDraftView {
  const data = kBiComboCardDataCaptured[card.id];
  if (data.state !== BiCardDataState.Ready) throw new Error(card.id);
  return {
    id: `bidraft_${id.padEnd(26, '0')}`,
    title: card.title,
    spec: card.spec,
    viz: card.viz,
    result: data.result,
    parentDraftId: null,
    rationale: null,
    createdAt: card.createdAt,
    expiresAt: '2026-10-18T06:30:00.000Z',
  };
}
